import {
  resolvePostLoginDestination,
  type RoleDashboardUser,
} from '@/lib/role-dashboard';

export type SocialLoginAuthResponse = {
  require_password_setup?: boolean;
  onboarding_token?: string;
  token?: string;
  access_token?: string;
  user?: RoleDashboardUser;
  data?: {
    token?: string;
    user?: RoleDashboardUser;
  };
  message?: string;
  error?: string;
};

export type FirebaseUserLike = {
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
  displayName?: string | null;
  email?: string | null;
};

function hasEmailHint(value?: string | null): boolean {
  return Boolean(value && value.includes('@'));
}

/** Safe Google-auth diagnostics — never logs tokens, emails, or names. */
export function logGoogleAuthStep(
  step: string,
  detail?: Record<string, unknown>
): void {
  if (typeof console === 'undefined' || typeof console.info !== 'function') return;
  console.info('[google-auth]', { step, ...detail });
}

/**
 * After a verified Google/Apple (Firebase) login, never force /set-password.
 * A local password is optional for social accounts.
 * Users with must_change_password are routed by resolvePostLoginDestination.
 */
export function passwordSetupPathAfterSocialLogin(
  response?: SocialLoginAuthResponse | null
): string | null {
  void response;
  return null;
}

export function resolveAfterSocialLogin(
  user: RoleDashboardUser | null | undefined,
  options?: { redirectPath?: string }
): string {
  return resolvePostLoginDestination(user, { redirectPath: options?.redirectPath });
}

export function extractUserFromFirebaseLoginResponse(
  payload?: SocialLoginAuthResponse | null
): RoleDashboardUser | null {
  if (!payload || typeof payload !== 'object') return null;
  const user = payload.user ?? payload.data?.user;
  if (!user || typeof user !== 'object') return null;
  return user;
}

export function firebaseLoginIssuedSession(
  payload?: SocialLoginAuthResponse | null
): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const token = payload.token ?? payload.access_token ?? payload.data?.token;
  return typeof token === 'string' && token.trim().length > 20;
}

export function resolveDestinationAfterFirebaseLogin(
  payload: SocialLoginAuthResponse | null | undefined,
  authUser: RoleDashboardUser | null | undefined,
  options?: { redirectPath?: string }
): string {
  const user = authUser ?? extractUserFromFirebaseLoginResponse(payload);
  return resolveAfterSocialLogin(user, { redirectPath: options?.redirectPath });
}

export function getSocialAuthErrorMessage(
  err: unknown,
  provider: 'Google' | 'Apple'
): string {
  const raw = err as {
    code?: string;
    message?: string;
    response?: {
      status?: number;
      data?: { message?: string; error?: string | { message?: string } };
    };
  } | null;
  const code = String(raw?.code || '');
  const status = raw?.response?.status;
  const apiMessage =
    (typeof raw?.response?.data?.message === 'string' && raw.response.data.message) ||
    (typeof raw?.response?.data?.error === 'string' && raw.response.data.error) ||
    (typeof raw?.response?.data?.error === 'object' && raw.response.data.error?.message) ||
    '';

  if (code === 'auth/operation-not-allowed') {
    return `${provider} sign-in is disabled for this Firebase project. Enable it in Firebase Console -> Authentication -> Sign-in method.`;
  }
  if (code === 'auth/popup-closed-by-user') {
    return `${provider} sign-in was cancelled.`;
  }
  if (code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request') {
    return `${provider} popup was blocked. Allow popups for this site and try again.`;
  }
  if (code === 'auth/unauthorized-domain') {
    return `${provider} sign-in is blocked: this site is not an authorized Firebase domain.`;
  }
  if (code === 'auth/invalid-oauth-client-id') {
    return `${provider} sign-in is misconfigured (invalid OAuth client).`;
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return `An account already exists with this email using a different sign-in method.`;
  }
  if (code === 'auth/network-request-failed') {
    return `Network error during ${provider} sign-in. Check your connection and try again.`;
  }
  if (status === 401 || status === 403) {
    return apiMessage || `${provider} sign-in was rejected by the server.`;
  }
  if (status === 422) {
    return apiMessage || `${provider} sign-in could not be completed.`;
  }
  if (status && status >= 500) {
    return `Could not complete ${provider} sign-in (server error ${status}).`;
  }
  return String(apiMessage || raw?.message || `Could not sign in with ${provider}.`);
}

export function shouldFallbackToRedirect(code: string): boolean {
  return code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request';
}

export async function recoverFirebaseUserAfterPopupClose<T>(
  getCurrentUser: () => T | null | undefined,
  timeoutMs = 2500
): Promise<T | null> {
  const immediate = getCurrentUser();
  if (immediate) return immediate;

  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    const next = getCurrentUser();
    if (next) return next;
  }
  return null;
}

export async function completeEnthiosGoogleSession(options: {
  firebaseUser: FirebaseUserLike;
  firebaseLogin: (
    token: string,
    profile?: { name?: string; email?: string }
  ) => Promise<{ data?: SocialLoginAuthResponse; status?: number }>;
  checkAuth?: (opts?: { force?: boolean; reason?: string }) => Promise<void>;
  getAuthUser?: () => RoleDashboardUser | null | undefined;
  apiHost?: string;
  redirectPath?: string;
}): Promise<string> {
  logGoogleAuthStep('id_token_requested', { has_email: hasEmailHint(options.firebaseUser.email) });
  const idToken = await options.firebaseUser.getIdToken(true);
  logGoogleAuthStep('id_token_received', { present: Boolean(idToken && idToken.length > 20) });

  logGoogleAuthStep('backend_login_started', { api_host: options.apiHost || null });
  const res = await options.firebaseLogin(idToken, {
    name: options.firebaseUser.displayName || undefined,
    email: options.firebaseUser.email || undefined,
  });
  const payload = res?.data;
  logGoogleAuthStep('backend_login_completed', {
    status: res?.status ?? null,
    has_session: firebaseLoginIssuedSession(payload),
    has_user: Boolean(extractUserFromFirebaseLoginResponse(payload)),
    require_password_setup: Boolean(payload?.require_password_setup),
  });

  if (!firebaseLoginIssuedSession(payload) && !extractUserFromFirebaseLoginResponse(payload)) {
    throw new Error(
      payload?.message ||
        'Google sign-in succeeded, but EnthiosHub did not issue a session. Please try again.'
    );
  }

  if (options.checkAuth) {
    await options.checkAuth({ force: true, reason: 'google_login' });
  }
  const destination = resolveDestinationAfterFirebaseLogin(
    payload,
    options.getAuthUser?.() ?? null,
    { redirectPath: options.redirectPath }
  );
  logGoogleAuthStep('navigate', { destination });
  return destination;
}
